import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Autocomplete,
  MenuItem,
  Alert,
  CircularProgress,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import {
  Save as SaveIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
  ShoppingCart as ShoppingCartIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext, emitirEcfDgii } from 'context';
import { FP, fpInputSx } from 'components/financepro';
import { useEmpresaFiscal, useEmisorFiscal } from "lib/empresaFiscal";

const cardShellSx = {
  borderRadius: 2.5,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  border: `1px solid ${FP.color.border}`,
  overflow: 'hidden',
};

const filterHeaderSx = {
  px: { xs: 2, md: 3 },
  py: 1.6,
  borderBottom: `1px solid ${FP.color.border}`,
  bgcolor: '#FAFBFE',
  display: 'flex',
  alignItems: 'center',
  gap: 1.2,
};

const toNumber = (value) => parseFloat(String(value ?? '').replace(/,/g, '')) || 0;

const formatMoney = (value) =>
  (Number(value) || 0).toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const codigoTipoDocumento = (tipo) => {
  const desdeCodigo = String(tipo?.codigo || '').replace(/\D/g, '');
  if (desdeCodigo) return desdeCodigo;
  return (tipo?.nombre || '').split(':')[1]?.replace(/\D/g, '') || '';
};

const fechaDdMmYyyy = (valor) => {
  const texto = String(valor || '').trim();
  if (!texto) return '';
  const iso = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  const dmy = texto.match(/^(\d{2})[/-](\d{2})[/-](\d{4})/);
  if (dmy) return `${dmy[1]}-${dmy[2]}-${dmy[3]}`;
  return texto;
};

const detalleRespuestaEcf = (emision) => {
  const rd = emision?.respuesta_dgii || {};
  const deMensajes = (rd.mensajes || [])
    .map((m) => {
      if (typeof m === 'string') return m;
      const texto = m.valor || m.mensaje || m.descripcion || '';
      return `${texto}${m.codigo ? ` (${m.codigo})` : ''}`;
    })
    .filter((t) => String(t || '').trim());
  const extra = [rd.error, rd.detalle, rd.mensaje, rd.descripcion].filter((t) =>
    String(t || '').trim()
  );
  const xsd = (emision?.xsd_mensajes || []).filter(
    (m) => m && !String(m).toLowerCase().includes('no instalado')
  );
  return [...deMensajes, ...extra, ...xsd]
    .filter((t, i, arr) => t && arr.indexOf(t) === i)
    .join(' · ');
};

const labelSuplidor = (s) => {
  if (!s) return '';
  const rnc = String(s.rnc || '').trim();
  return `${s.id} — ${s.descrip || ''}${rnc ? ` (${rnc})` : ''}`;
};

const labelFormaPago = (f) => {
  if (!f) return '';
  const nombre = String(f.documento || '').trim() || '(sin nombre)';
  const cuenta = String(f.cuenta_contable || '').trim();
  return cuenta ? `${nombre} · ${cuenta}` : nombre;
};

/** DGII IndicadorBienoServicio: 1 = bien (compra), 2 = servicio. */
const TIPO_COMPRA = '1';
const TIPO_SERVICIO = '2';

/**
 * Captura de Comprobante para exportaciones (E46).
 * ITBIS 0% (indicador 3 / gravado I3, cont2510). Sin ISR. Razón social obligatoria; RNC dominicano opcional.
 */
const FormularioExportacion = ({
  open = false,
  onClose,
  puedeModificar,
  tiposDocumentosFiscales,
  tipoDocumento,
  onTipoDocumentoChange,
  onCreada,
}) => {
  // Multi-empresa: empresa fiscal elegida en la barra lateral (1 sin el modo).
  const { empresa: empresaFiscal } = useEmpresaFiscal();
  const emisorFiscal = useEmisorFiscal();
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const {
    usuarioLog,
    claveLog,
    comprobanteelectronico,
    nombreNegocio,
    rnc_empresa,
    direccion_empresa,
  } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';
  const emisionElectronica =
    String(comprobanteelectronico || '').trim().toUpperCase() === 'S';

  const hoy = new Date().toISOString().slice(0, 10);
  const [fecha, setFecha] = useState(hoy);
  const [suplidorSel, setSuplidorSel] = useState(null);
  const [formaPagoSel, setFormaPagoSel] = useState(null);
  const [referenciaPago, setReferenciaPago] = useState('');
  const [tasa, setTasa] = useState('1.00');
  const [tipoBienServicio, setTipoBienServicio] = useState(TIPO_COMPRA);
  const [concepto, setConcepto] = useState('');
  const [monto, setMonto] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [suplidores, setSuplidores] = useState([]);
  const [formasPago, setFormasPago] = useState([]);
  const [cargandoFormas, setCargandoFormas] = useState(false);

  const credenciales = useMemo(
    () => ({
      id_empresa: selectedCompany,
      usuario: usuarioLog || '',
      password: claveLog || '', empresa: empresaFiscal,
    }),
    [selectedCompany, usuarioLog, claveLog]
  );

  const cargarMaestros = useCallback(async () => {
    if (!usuarioLog) return;
    const post = async (url) => {
      const res = await fetch(`${servidor}${url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credenciales),
      });
      const data = await res.json().catch(() => ({}));
      return { ok: res.ok, data };
    };
    setCargandoFormas(true);
    try {
      const [supRes, formasApi, formasSinApi] = await Promise.all([
        post('/contabilidadbackend_listarsuplidores'),
        post('/api/configuracionbackend_listarformasdepago'),
        post('/configuracionbackend_listarformasdepago'),
      ]);
      setSuplidores(supRes.data.suplidores || []);
      const formas = formasApi.ok
        ? formasApi.data.formas_pago
        : formasSinApi.ok
          ? formasSinApi.data.formas_pago
          : [];
      setFormasPago(Array.isArray(formas) ? formas : []);
      if (!supRes.ok) showError('No se pudieron cargar los suplidores');
      if (!formasApi.ok && !formasSinApi.ok) {
        showError(
          formasApi.data.Error ||
            formasSinApi.data.Error ||
            'No se pudieron cargar las formas de pago'
        );
      }
    } catch (_e) {
      showError('No se pudieron cargar suplidores o formas de pago');
    } finally {
      setCargandoFormas(false);
    }
  }, [usuarioLog, servidor, credenciales, showError]);

  useEffect(() => {
    if (open) cargarMaestros();
  }, [open, cargarMaestros]);

  const subtotal = useMemo(() => toNumber(monto), [monto]);
  const itbis = 0;
  const retencion = 0;
  const totalPagar = subtotal;

  const requiereReferencia =
    String(formaPagoSel?.necesita_referencia || 'N').toUpperCase() === 'S';

  useEffect(() => {
    if (!requiereReferencia) setReferenciaPago('');
  }, [requiereReferencia]);

  const docFiscal = useMemo(
    () => tiposDocumentosFiscales.find((t) => t.nombre === tipoDocumento) || null,
    [tiposDocumentosFiscales, tipoDocumento]
  );

  const limpiarFormulario = () => {
    setFecha(hoy);
    setSuplidorSel(null);
    setFormaPagoSel(null);
    setReferenciaPago('');
    setTasa('1.00');
    setTipoBienServicio(TIPO_COMPRA);
    setConcepto('');
    setMonto('');
  };

  const descargarArchivoECF = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'exportacion_ecf.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const descargarXmlECF = (xmlTexto, filename) => {
    descargarArchivoECF(new Blob([xmlTexto], { type: 'text/xml' }), filename);
  };

  const guardarCompra = async () => {
    if (!puedeModificar) {
      showError('No tiene permisos para crear exportaciones.');
      return;
    }
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    if (!suplidorSel?.id) {
      showError('Seleccione el cliente / comprador');
      return;
    }
    // E46: razón social obligatoria; RNC dominicano opcional (IdentificadorExtranjero).
    if (!tipoBienServicio) {
      showError('Indique si es una compra o un servicio');
      return;
    }
    if (!concepto.trim()) {
      showError('El concepto es requerido');
      return;
    }
    if (subtotal <= 0) {
      showError('El monto debe ser mayor que cero');
      return;
    }
    if (!tipoDocumento || !docFiscal) {
      showError('Seleccione el tipo de documento fiscal E46');
      return;
    }
    if (!emisionElectronica) {
      showError('La empresa no tiene habilitada la emisión de comprobantes electrónicos');
      return;
    }
    if (!formaPagoSel?.documento) {
      showError('Seleccione la forma de pago');
      return;
    }
    if (requiereReferencia && !referenciaPago.trim()) {
      showError('La forma de pago requiere una referencia');
      return;
    }

    setGuardando(true);
    try {
      const response = await fetch(
        `${servidor}/api/facturaselectronicasdgii_crearexportacionelectronica`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            usuario: usuarioLog || '',
            password: claveLog || '', empresa: empresaFiscal,
            fecha,
            suplidor_id: String(suplidorSel.id),
            descripcion: concepto.trim(),
            tipo_bien_servicio: tipoBienServicio,
            monto: subtotal,
            tasa: toNumber(tasa) || 1,
            tipo_pago: 'Contado',
            banco_referencia: formaPagoSel
              ? `${formaPagoSel.documento}${referenciaPago.trim() ? ` · ${referenciaPago.trim()}` : ''}`
              : '',
            tipo_documento: tipoDocumento,
            comprobanteelectronico: 'S',
            codigocomprobante: codigoTipoDocumento(docFiscal) || '46',
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'No se pudo guardar la exportación');
        return;
      }

      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      let filename = 'exportacion_ecf.xlsx';
      const match = disposition.match(/filename="?([^"]+)"?/);
      if (match && match[1]) filename = match[1];
      const ncf = response.headers.get('X-Comprobante-Fiscal') || '';
      const fechaVencimiento = fechaDdMmYyyy(response.headers.get('X-Fecha-Valida') || '');
      const numero = parseInt(response.headers.get('X-Numero-Desembolso') || '0', 10);
      const identExterior = String(suplidorSel.rnc || '')
        .replace(/[^A-Za-z0-9]/g, '')
        .slice(0, 20);
      const rncComprador = response.headers.get('X-Rnc-Comprador') || identExterior;
      const razonSocial = response.headers.get('X-Razon-Social') || suplidorSel.descrip || '';
      const subtotalHdr = toNumber(response.headers.get('X-Subtotal')) || subtotal;
      const itbisHdr = toNumber(response.headers.get('X-Itbis')) || itbis;
      const retencionHdr = toNumber(response.headers.get('X-Retencion')) || retencion;

      descargarArchivoECF(blob, filename);
      showSuccess(`Desembolso ${numero} guardado${ncf ? ` · ${ncf}` : ''}`);

      const payloadEmision = {
        comprobanteelectronico: emisionElectronica ? 'S' : 'N',
        rnc: rncComprador,
        razonsocial: razonSocial,
        comprobante: ncf,
        tipocomprobante: tipoDocumento,
        codigocomprobante: codigoTipoDocumento(docFiscal) || '46',
        identificador_extranjero: identExterior,
        fechavencimientosecuencia: fechaVencimiento,
        tipo_pago: '1',
        items: [
          {
            item: concepto.trim(),
            cantidad: 1,
            valor: subtotalHdr,
            descuento: 0,
            tasaitbis: 0,
            montoitbisretenido: 0,
            montoisrretenido: 0,
            indicadorbienoservicio: tipoBienServicio,
          },
        ],
        password: claveLog, empresa: empresaFiscal,
        usuario: usuarioLog,
        rncemisor: emisorFiscal.rnc || '',
        razonsocialemisor: emisorFiscal.nombre || '',
        direccionemisor: emisorFiscal.direccion || '',
      };

      const emision = await emitirEcfDgii(servidor, payloadEmision);
      const estadoEcf = String(emision?.estado || '').trim().toLowerCase();
      const dgiiFallido = estadoEcf === 'rechazado' || estadoEcf === 'error';
      const encfUsado = String(emision?.encf || ncf || '').trim();

      const sincronizarEstado = async (estado, fechaFirma = '') => {
        if (!encfUsado || !estado) return;
        try {
          await fetch(`${servidor}/api/facturaselectronicasdgii_actualizarestadoecf`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              usuario: usuarioLog || '',
              password: claveLog || '', empresa: empresaFiscal,
              comprobante: encfUsado,
              estado,
              fecha_firma: fechaFirma,
            }),
          });
        } catch (_ignored) {
          /* best-effort */
        }
      };

      if (emision.ok) {
        descargarXmlECF(emision.xml, `${emision.encf || 'ecf'}.xml`);
        if (emision.xml_rfce) {
          descargarXmlECF(emision.xml_rfce, `${emision.encf || 'ecf'}-resumen.xml`);
        }
        const etiquetas = {
          aceptado: 'aceptado por la DGII',
          aceptado_condicional: 'aceptado condicional por la DGII',
          en_proceso: `en proceso en la DGII${emision.track_id ? ` (TrackId ${emision.track_id})` : ''}`,
          rechazado: 'RECHAZADO por la DGII',
          pendiente_envio: 'firmado; DGII inaccesible, se reenviará',
          error: 'firmado; el envío a la DGII falló',
        };
        const detalleDgii = detalleRespuestaEcf(emision);
        const mensaje =
          `e-CF ${emision.encf} ${etiquetas[estadoEcf] || emision.estado}` +
          ` · código ${emision.codigo_seguridad}` +
          (detalleDgii ? ` — DGII: ${detalleDgii}` : '');
        if (dgiiFallido) showError(mensaje);
        else showSuccess(mensaje);
        await sincronizarEstado(
          estadoEcf || 'aceptado',
          emision.fecha_firma || emision.fecha || new Date().toISOString().slice(0, 10)
        );
      } else {
        showError(emision.error || 'No se pudo emitir el e-CF ante la DGII');
        await sincronizarEstado('error');
      }

      if (!emision.ok || dgiiFallido) {
        try {
          await fetch(`${servidor}/api/facturaselectronicasdgii_revertircompraelectronica`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              usuario: usuarioLog || '',
              password: claveLog || '', empresa: empresaFiscal,
              numero,
              comprobante: encfUsado,
            }),
          });
        } catch (_ignored) {
          /* best-effort */
        }
        showError(
          `Desembolso ${numero} revertido (e-CF rechazado o con error). El próximo usa la secuencia siguiente.`
        );
        if (typeof onCreada === 'function') onCreada({ fallido: true, ncf: encfUsado });
        return;
      }

      const compraVista = {
        numero,
        descripcion: concepto.trim(),
        total: subtotalHdr,
        itbis: itbisHdr,
        retencion: retencionHdr,
        suplidor: suplidorSel.id,
        suplidor_nombre: razonSocial,
        rnc: rncComprador,
      };
      limpiarFormulario();
      if (typeof onClose === 'function') onClose();
      if (typeof onCreada === 'function') {
        onCreada({
          compras: [compraVista],
          ncf,
          emisionData: {
            ...emision,
            fechavencimientosecuencia: fechaVencimiento,
            fechavencimiento: fechaVencimiento,
          },
          rncComprador,
          razonSocialComprador: razonSocial,
        });
      }
    } catch (_e) {
      showError('Error de conexión al guardar el exportación');
    } finally {
      setGuardando(false);
    }
  };

  const cerrar = () => {
    if (guardando) return;
    if (typeof onClose === 'function') onClose();
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={cerrar}
      fullWidth
      maxWidth={false}
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          width: 'min(96vw, 1600px)',
          height: '94vh',
          maxHeight: '94vh',
          m: 1,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 800,
          color: FP.color.textPrimary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1.5,
          borderBottom: `1px solid ${FP.color.border}`,
        }}
      >
        Nueva exportación
        <IconButton onClick={cerrar} disabled={guardando} size="small" aria-label="Cerrar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ bgcolor: FP.color.surface, p: { xs: 2, md: 3 }, height: '100%' }}>
        <Card elevation={0} sx={{ ...cardShellSx, mb: 2.5, mt: 1 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PersonIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Datos del documento
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            {!puedeModificar && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Solo lectura: no puede emitir exportaciones.
              </Alert>
            )}
            {!emisionElectronica && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                La empresa no tiene habilitada la emisión de comprobantes electrónicos.
              </Alert>
            )}
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  label="Fecha"
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                  fullWidth
                  disabled={!puedeModificar}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={5}>
                <Autocomplete
                  options={suplidores}
                  value={suplidorSel}
                  onChange={(_e, v) => setSuplidorSel(v)}
                  getOptionLabel={labelSuplidor}
                  isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                  disabled={!puedeModificar}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cliente / comprador"
                      placeholder="Buscar cliente"
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  select
                  label="Documento fiscal"
                  value={tipoDocumento}
                  onChange={(e) => onTipoDocumentoChange(e.target.value)}
                  fullWidth
                  disabled={!puedeModificar}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                >
                  <MenuItem value="">Seleccione…</MenuItem>
                  {tiposDocumentosFiscales.map((t) => (
                    <MenuItem key={t.id || t.nombre} value={t.nombre}>
                      {t.nombre}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Tipo de pago"
                  value="Contado"
                  fullWidth
                  disabled
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={5}>
                <Autocomplete
                  options={formasPago}
                  loading={cargandoFormas}
                  value={formaPagoSel}
                  onChange={(_e, v) => {
                    setFormaPagoSel(v);
                    setReferenciaPago('');
                  }}
                  getOptionLabel={labelFormaPago}
                  isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                  disabled={!puedeModificar || cargandoFormas}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Forma de pago"
                      placeholder={
                        cargandoFormas
                          ? 'Cargando…'
                          : formasPago.length
                            ? 'Seleccione forma de pago'
                            : 'No hay formas de pago definidas'
                      }
                      helperText={
                        formaPagoSel
                          ? requiereReferencia
                            ? 'Debe completar la referencia'
                            : 'No requiere referencia'
                          : ' '
                      }
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Referencia al pago"
                  value={requiereReferencia ? referenciaPago : ''}
                  onChange={(e) => setReferenciaPago(e.target.value)}
                  fullWidth
                  disabled={!puedeModificar || !requiereReferencia}
                  required={requiereReferencia}
                  placeholder={requiereReferencia ? 'Nº cheque, transferencia, etc.' : ''}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="Tasa"
                  value={tasa}
                  onChange={(e) => setTasa(e.target.value)}
                  fullWidth
                  disabled={!puedeModificar}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  label="Retención ISR"
                  value="27%"
                  fullWidth
                  disabled
                  helperText="Fijo en exportaciones"
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ShoppingCartIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Detalle de la exportación
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  label="Tipo"
                  value={tipoBienServicio}
                  onChange={(e) => setTipoBienServicio(e.target.value)}
                  fullWidth
                  disabled={!puedeModificar}
                  InputLabelProps={{ shrink: true }}
                  helperText="Compra = bien · Servicio = servicio"
                  sx={fpInputSx}
                >
                  <MenuItem value={TIPO_COMPRA}>Compra</MenuItem>
                  <MenuItem value={TIPO_SERVICIO}>Servicio</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <TextField
                  label="Concepto"
                  value={concepto}
                  onChange={(e) => setConcepto(e.target.value)}
                  fullWidth
                  disabled={!puedeModificar}
                  InputLabelProps={{ shrink: true }}
                  placeholder={
                    tipoBienServicio === TIPO_COMPRA
                      ? 'Descripción de la compra'
                      : 'Descripción del servicio'
                  }
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Monto"
                  value={monto}
                  onChange={(e) => setMonto(e.target.value)}
                  fullWidth
                  disabled={!puedeModificar}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  sx={fpInputSx}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 2 }} justifyContent="flex-end">
              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem' }}>
                  Subtotal
                </Typography>
                <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary }}>
                  ${formatMoney(subtotal)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem' }}>
                  ITBIS 0% (I3)
                </Typography>
                <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary }}>
                  ${formatMoney(itbis)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem' }}>
                  Total
                </Typography>
                <Typography sx={{ fontWeight: 800, color: FP.color.navy, fontSize: '1.15rem' }}>
                  ${formatMoney(totalPagar)}
                </Typography>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', gap: 1.25, justifyContent: 'flex-end', mt: 2.5, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={limpiarFormulario}
                disabled={guardando}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderColor: FP.color.border,
                  color: FP.color.textPrimary,
                }}
              >
                Limpiar
              </Button>
              <Button
                variant="contained"
                startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={guardarCompra}
                disabled={
                  !puedeModificar ||
                  guardando ||
                  subtotal <= 0 ||
                  !concepto.trim() ||
                  !tipoDocumento ||
                  !emisionElectronica
                }
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  bgcolor: FP.color.navy,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                }}
              >
                Guardar y firmar e-CF
              </Button>
            </Box>
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
};

export default FormularioExportacion;
